import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { CheckSquare, AlertTriangle } from "lucide-react";
import SectionCard from "../components/ui/cards/SectionCard";
import ApprovalFilterBar, { ApprovalFilterState, Option } from "../components/ui/filters/ApprovalFilterBar";
import ApprovalTable from "../components/ui/tables/ApprovalTable";
import PageHeader from "../components/layout/PageHeader";
import { useExpenses } from "../hooks/useExpenses";
import { useAuthContext } from "../context/AuthContext";
import { updateApprovalStatus } from "../utils/approval/updateApprovalStatus";
import Button from '../ui/Button';

const ApprovalPage: React.FC = () => {
  const { expenses, refresh } = useExpenses();
  const { user } = useAuthContext();
  const [filters, setFilters] = useState<ApprovalFilterState>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    action: "approved" | "rejected" | null;
    count: number;
  }>({ isOpen: false, action: null, count: 0 });

  const unitOptions: Option[] = useMemo(() => {
    const vals = Array.from(new Set(expenses.map(e => e.unit)));
    return vals.map(v => ({ label: v, value: v }));
  }, [expenses]);

  const typeOptions: Option[] = useMemo(() => {
    const vals = Array.from(new Set(expenses.map(e => e.type)));
    return vals.map(v => ({ label: v, value: v }));
  }, [expenses]);

  const filtered = useMemo(() => {
    return expenses.filter(e => {
      if (!filters.status && e.approvalStatus === "approved") return false;
      if (filters.status && e.approvalStatus !== filters.status) return false;
      if (filters.unit && e.unit !== filters.unit) return false;
      if (filters.type && e.type !== filters.type) return false;
      if (filters.start && new Date(e.date) < new Date(filters.start)) return false;
      if (filters.end && new Date(e.date) > new Date(filters.end)) return false;
      return true;
    });
  }, [expenses, filters]);

  const handleApprove = async (id: string) => {
    await updateApprovalStatus(id, "approved", user?.email || "");
    refresh();
  };

  const handleReject = async (id: string) => {
    await updateApprovalStatus(id, "rejected", user?.email || "");
    refresh();
  };

  // Show confirmation dialog for bulk actions
  const showConfirmDialog = (action: "approved" | "rejected") => {
    if (selected.length === 0) {
      toast.warning("No expenses selected");
      return;
    }
    setConfirmDialog({ isOpen: true, action, count: selected.length });
  };

  // Bulk action handler with batch processing and single summary toast
  const bulkAction = async (status: "approved" | "rejected") => {
    setConfirmDialog({ isOpen: false, action: null, count: 0 });

    const actionLabel = status === "approved" ? "approve" : "reject";
    const toastId = toast.info(`Processing ${selected.length} expense${selected.length !== 1 ? 's' : ''}...`, {
      autoClose: false,
    });

    try {
      // Use Promise.allSettled for parallel batch processing
      const results = await Promise.allSettled(
        selected.map(id => updateApprovalStatus(id, status, user?.email || ""))
      );

      // Count successful and failed operations
      const successful = results.filter(r =>
        r.status === 'fulfilled' && r.value.success
      ).length;
      const failed = results.length - successful;

      toast.dismiss(toastId);

      // Show single summary toast
      if (failed === 0) {
        toast.success(`Successfully ${status} ${successful} expense${successful !== 1 ? 's' : ''}`);
      } else if (successful === 0) {
        toast.error(`Failed to ${actionLabel} all ${failed} expense${failed !== 1 ? 's' : ''}`);
      } else {
        toast.warning(`${status.charAt(0).toUpperCase() + status.slice(1)} ${successful}, Failed ${failed} out of ${results.length} expenses`);
      }

      setSelected([]);
      refresh();
    } catch (error) {
      toast.dismiss(toastId);
      console.error(`Bulk ${actionLabel} error:`, error);
      toast.error(`An error occurred during bulk ${actionLabel}`);
    }
  };

  return (
    <div className="p-4">
      <PageHeader
        icon={<CheckSquare size={24} />}
        title="Expense Approvals"
        subtitle="Review and approve pending expense submissions"
      />

      <SectionCard
        title="Pending Approvals"
        subtitle={`${filtered.length} expense${filtered.length !== 1 ? 's' : ''} to review`}
        actions={
          selected.length > 0 && (
            <div className="flex gap-2">
              <Button
                variant="success"
                onClick={() => showConfirmDialog("approved")}
                size="sm"
                title={`Approve ${selected.length} selected expense${selected.length !== 1 ? 's' : ''}`}
              >
                Approve ({selected.length})
              </Button>
              <Button
                variant="error"
                onClick={() => showConfirmDialog("rejected")}
                size="sm"
                title={`Reject ${selected.length} selected expense${selected.length !== 1 ? 's' : ''}`}
              >
                Reject ({selected.length})
              </Button>
            </div>
          )
        }
      >
        <ApprovalFilterBar
          state={filters}
          onChange={setFilters}
          unitOptions={unitOptions}
          typeOptions={typeOptions}
        />
        <ApprovalTable
          data={filtered}
          selected={selected}
          onSelect={setSelected}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      </SectionCard>

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && confirmDialog.action && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-elev-3 max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-start gap-4 mb-4">
              <div className={`p-3 rounded-full ${
                confirmDialog.action === "approved"
                  ? "bg-green-100"
                  : "bg-red-100"
              }`}>
                <AlertTriangle
                  size={24}
                  className={
                    confirmDialog.action === "approved"
                      ? "text-green-600"
                      : "text-red-600"
                  }
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Confirm Bulk {confirmDialog.action === "approved" ? "Approval" : "Rejection"}
                </h3>
                <p className="text-gray-600 text-sm">
                  Are you sure you want to {confirmDialog.action === "approved" ? "approve" : "reject"}{" "}
                  <span className="font-semibold">{confirmDialog.count}</span>{" "}
                  expense{confirmDialog.count !== 1 ? 's' : ''}?
                </p>
                <p className="text-gray-500 text-xs mt-2">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmDialog({ isOpen: false, action: null, count: 0 })}
              >
                Cancel
              </Button>
              <Button
                variant={confirmDialog.action === "approved" ? "success" : "error"}
                size="sm"
                onClick={() => bulkAction(confirmDialog.action!)}
              >
                {confirmDialog.action === "approved" ? "Approve" : "Reject"} All
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalPage;
